import React, { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box, Label, Input, FormField, Tooltip } from 'pcln-design-system'
import { Search as SearchIcon, Warning as WarningIcon } from 'pcln-icons'
import Downshift from 'downshift'
import { useMachine } from '@xstate/react'
import fetch from 'isomorphic-unfetch'
import CONFIG from 'isomorphic-config'
import typeAheadInputStyle from './StyledInput'
import Dropdown from './Dropdown'
import BootstrapDataContext from '../../context/BootstrapDataContext'
import { PopoverContainer } from '../Popover'
import { getErrorFromKey } from '../../utils/error-helpers'
import geoMachine from '../../machines/currentLocationMachine'
import useTypeAheadForDisplayName from './useTypeAheadForDisplayName'
import useRecentSearches from '../CustomHooks/useRecentSearches'
import {
  AIR_SEARCH_DATA,
  CAR_SEARCH_DATA,
  HOTEL_SEARCH_DATA,
  PACKAGE_SEARCH_DATA
} from '../../utils/local-forage-helper'

const errorProps = {
  id: `typeahead-error`,
  style: { zIndex: 1 },
  bottom: true,
  right: true,
  color: 'error',
  role: 'alert'
}

const mapTypeToCode = {
  AIRPORT: 'A',
  CITY: 'C',
  HOTEL: 'H',
  POI: 'P'
}

const recentSearchesKey = {
  cars: CAR_SEARCH_DATA,
  hotels: HOTEL_SEARCH_DATA,
  flights: AIR_SEARCH_DATA,
  packages: PACKAGE_SEARCH_DATA
}

const TypeAhead = React.forwardRef(
  (
    {
      label,
      placeholder,
      searchProduct,
      searchKey,
      setValue,
      defaultSelectedItem,
      errors,
      disableShadowState,
      focusNextElement,
      onItemSelect,
      showCurrentLocation
    },
    ref
  ) => {
    const { isMobile = false } = useContext(BootstrapDataContext)
    const handleInputKeyDown = ({ event, isOpen, selectHighlightedItem }) => {
      if (isOpen && ['Tab'].includes(event.key)) {
        event.preventDefault()
        selectHighlightedItem()
        focusNextElement()
      }
    }

    const typeAheadEndpoint = searchProduct
    const currentRecentSearchesKey = recentSearchesKey[typeAheadEndpoint]
    const [recentStartLocations, recentEndLocations] = useRecentSearches(
      currentRecentSearchesKey
    )

    const recentSearches = searchKey.includes('startLocation')
      ? recentStartLocations
      : recentEndLocations

    const handleTypeAheadFeedback = ({
      entered,
      id,
      itemName,
      displayName,
      type,
      selectedPosition
    }) => {
      // swagger for feedback call api http://ny-htlindex-001:8080/typeahead/index/swagger-ui.html#/feedback-controller/typeaheadFeedbackWithSelectedPositionUsingGET
      const url = `${
        CONFIG.client.typeahead.feedback[typeAheadEndpoint]
      }/${id}/${encodeURIComponent(entered)}/${encodeURIComponent(
        itemName || displayName
      )}/${selectedPosition}`
      const placeType = mapTypeToCode[type] ?? type
      const queryParams = `language=en&locale=en-US&placeType=${placeType}`
      fetch(`${url}?${queryParams}`)
    }

    const [isPopoverOpen, setIsPopoverOpen] = useState(false)

    const handleSelect = disableShadowEffect => (
      selectedItem,
      { reset, highlightedIndex }
    ) => {
      setValue(searchKey, selectedItem)
      onItemSelect(selectedItem)
      reset()
      if (selectedItem) {
        handleTypeAheadFeedback({
          ...selectedItem,
          selectedPosition: highlightedIndex
        })
        disableShadowEffect()
        setIsPopoverOpen(false)
      }
    }

    const stateReducer = (state, changes) => {
      switch (changes.type) {
        case Downshift.stateChangeTypes.keyDownEnter:
        case Downshift.stateChangeTypes.clickItem:
        case Downshift.stateChangeTypes.mouseUp: // case for item selection on input blur
        case undefined: // handle case for item selection when you use TAB key. This seems to be a bug in Downshift. As per the type system, changes.type cannot be null
        case Downshift.stateChangeTypes.unknown: {
          const { highlightedIndex, inputValue } = state
          if (
            state.isOpen &&
            Object.prototype.hasOwnProperty.call(changes, 'isOpen')
          ) {
            return {
              ...changes,
              // persist value of highlightedIndex after item selection
              // Normally highlightedIndex changes to defaultHighlightedIndex after selection
              highlightedIndex,
              inputValue
            }
          }
          return changes
        }
        default:
          return changes
      }
    }

    const itemDisplayNameTransform = useTypeAheadForDisplayName()
    const itemToString = i => {
      return itemDisplayNameTransform?.(i) ?? i?.itemName ?? ''
    }

    const errorMessage = getErrorFromKey(searchKey)(errors)

    const [current, send, service] = useMachine(geoMachine)

    const currentLocationProps = {
      current,
      showCurrentLocation,
      handleRequestCurrentLocationClick: () => {
        send('REQUEST')
      }
    }

    useEffect(() => {
      const subscription = service.subscribe(state => {
        if (state.value === 'resolved') {
          disableShadowState()
          setValue(searchKey, state.context.cityData)
          onItemSelect(state.context.cityData)
          setIsPopoverOpen(false)
        }
      })

      return () => subscription.unsubscribe()
    }, [service, setValue, searchKey, onItemSelect, disableShadowState])

    return (
      <Downshift
        stateReducer={stateReducer}
        selectedItem={defaultSelectedItem}
        defaultHighlightedIndex={0}
        onSelect={handleSelect(disableShadowState)}
        itemToString={itemToString}
        id={`${searchKey}-typeahead-downshift-container`}
      >
        {({
          getLabelProps,
          getInputProps,
          getRootProps,
          openMenu,
          clearSelection,
          selectHighlightedItem,
          isOpen,
          inputValue,
          highlightedIndex,
          ...restProps
        }) => (
          <Box
            width={1}
            style={{ position: 'relative' }}
            {...getRootProps({
              refKey: 'ref'
            })}
          >
            <PopoverContainer
              open={isPopoverOpen}
              onDismiss={() => setIsPopoverOpen(false)}
            >
              <Box
                role="combobox"
                aria-expanded={isOpen ? 'true' : 'false'}
                aria-owns="typeahead-list"
                aria-haspopup="listbox"
              >
                <FormField>
                  <Label
                    pb={0}
                    autoHide={label === placeholder}
                    width="auto"
                    {...getLabelProps()}
                  >
                    {label}
                  </Label>
                  <SearchIcon color="primary" size={24} />
                  <Input
                    color={errorMessage ? 'red' : undefined}
                    ref={ref}
                    id={`${searchKey}-typeahead-input`}
                    data-testid={`${searchKey}-typeahead-input`}
                    autoComplete="off"
                    {...getInputProps({
                      placeholder,
                      name: searchKey,
                      style: typeAheadInputStyle(inputValue, label, isMobile),
                      pt: 0,
                      isOpen,
                      onKeyDown: event => {
                        handleInputKeyDown({
                          event,
                          selectHighlightedItem,
                          isOpen,
                          highlightedIndex
                        })
                        setIsPopoverOpen(true)
                      },
                      onKeyUp: event => {
                        if (event.keyCode === 27) {
                          setIsPopoverOpen(false)
                        }
                      },
                      onChange: event => {
                        if (event.target.value === '') {
                          clearSelection()
                        }
                      },
                      onClick: event => {
                        event.preventDefault()
                        if (event.target.value !== '') {
                          event.target.select()
                          openMenu()
                        }
                        setIsPopoverOpen(true)
                      },
                      onBlur: () => {
                        setIsPopoverOpen(false)
                      },
                      onFocus: () => {
                        if (showCurrentLocation || recentSearches.length) {
                          openMenu()
                        }
                        setIsPopoverOpen(true)
                      }
                    })}
                  />
                  {errorMessage && <WarningIcon color="error" size={20} />}
                </FormField>
                {errorMessage && (
                  <Tooltip {...errorProps}>{errorMessage}</Tooltip>
                )}
                {/* For accessibility reasons for desktop, the results are inside the container with role='combobox' */}
                {!isMobile && (
                  <Dropdown
                    isOpen={isOpen}
                    searchKey={searchKey}
                    searchValue={inputValue}
                    isMobile={isMobile}
                    typeAheadEndpoint={typeAheadEndpoint}
                    highlightedIndex={highlightedIndex}
                    recentSearches={recentSearches}
                    label={label}
                    {...restProps}
                  />
                )}
              </Box>
              {isMobile && (
                <Dropdown
                  isOpen={isOpen}
                  searchKey={searchKey}
                  searchValue={inputValue}
                  isMobile={isMobile}
                  typeAheadEndpoint={typeAheadEndpoint}
                  highlightedIndex={highlightedIndex}
                  recentSearches={recentSearches}
                  label={label}
                  {...currentLocationProps}
                  {...restProps}
                />
              )}
            </PopoverContainer>
          </Box>
        )}
      </Downshift>
    )
  }
)

TypeAhead.defaultProps = {
  defaultSelectedItem: undefined,
  setValue: () => {},
  onItemSelect: () => {},
  focusNextElement: () => {},
  showCurrentLocation: false
}

TypeAhead.propTypes = {
  searchProduct: PropTypes.oneOf(['flights', 'hotels', 'cars', 'packages'])
    .isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  searchKey: PropTypes.string.isRequired,
  setValue: PropTypes.func,
  defaultSelectedItem: PropTypes.object,
  errors: PropTypes.object.isRequired,
  disableShadowState: PropTypes.func.isRequired,
  onItemSelect: PropTypes.func,
  focusNextElement: PropTypes.func,
  showCurrentLocation: PropTypes.bool
}

export default TypeAhead
